import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useTranslation } from 'hooks/useTranslation';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { usePayPeriodStore } from 'pages/ReportsPage/store/payPeriod.store';
import { isSinglePayPeriodRange } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/PayPeriodDateInput.utils';
import { useOvertimeDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/store/overtimeDashboard.store';
import { useOvertimeKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/useOvertimeKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { OtByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OtByPositionChart';
import { OtByTeamMemberTypeChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OtByTeamMemberTypeChart';
import { OtTrendChart } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OtTrendChart';
import { OtTrendPeriodSelect } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OtTrendPeriodSelect';
import { TopOvertimeIndividualsTable } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/TopOvertimeIndividualsTable';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  DashboardErrorContainer,
  WidgetGrid,
  FullWidthSection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OvertimeDashboard.styled';

export const OvertimeDashboard: React.FC = () => {
  const { t } = useTranslation();
  const fetchData = useOvertimeDashboardStore((s) => s.fetchData);
  const reset = useOvertimeDashboardStore((s) => s.reset);
  const isLoading = useOvertimeDashboardStore((s) => s.isLoading);
  const trendLoading = useOvertimeDashboardStore((s) => s.trendLoading);
  const error = useOvertimeDashboardStore((s) => s.error);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const payPeriodCommunityId = usePayPeriodStore((s) => s.communityId);
  const payPeriodPositionId = usePayPeriodStore((s) => s.positionId);
  const payPeriodConfig = usePayPeriodStore((s) => s.config);
  const kpiCards = useOvertimeKpiCards();
  const hasCohortSelection = locationIds?.length === 1 && positions?.length === 1;
  const isPayPeriodRangeReady =
    hasCohortSelection &&
    payPeriodCommunityId === locationIds?.[0] &&
    payPeriodPositionId === positions?.[0] &&
    !!payPeriodConfig &&
    isSinglePayPeriodRange(dateRange, payPeriodConfig);
  const showSkeleton = isLoading || !isPayPeriodRangeReady;

  useEffect(() => {
    if (!isPayPeriodRangeReady) return;
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions, isPayPeriodRangeReady]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label={t('reports:OVERTIME_DASHBOARD_TITLE')}>
      <DashboardHeader>
        <DashboardTitle>{t('reports:OVERTIME_DASHBOARD_TITLE')}</DashboardTitle>
      </DashboardHeader>
      <FilterBar variant="payPeriod" />
      <DashboardContent>
        {error ? (
          <div aria-live="assertive">
            <DashboardErrorContainer>{error}</DashboardErrorContainer>
          </div>
        ) : showSkeleton ? (
          <DashboardSkeleton kpiCount={2} widgetCount={4} />
        ) : (
          <Fragment>
            <KpiStatCardRow cards={kpiCards} fullWidth />
            <WidgetGrid>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_BY_POSITION_TITLE')}
                subtitle={t('reports:OVERTIME_BY_POSITION_SUBTITLE')}
              >
                <OtByPositionChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_BY_TYPE_TITLE')}
                subtitle={t('reports:OVERTIME_BY_TYPE_SUBTITLE')}
              >
                <OtByTeamMemberTypeChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_TREND_TITLE')}
                subtitle={t('reports:OVERTIME_TREND_SUBTITLE')}
                headerAction={<OtTrendPeriodSelect />}
                loading={trendLoading}
              >
                <OtTrendChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title={t('reports:OVERTIME_INDIVIDUALS_TITLE')}
                subtitle={t('reports:OVERTIME_INDIVIDUALS_SUBTITLE')}
              >
                <TopOvertimeIndividualsTable />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
